import { BadgeCheck, Eye, Undo2, Upload } from 'lucide-react'
import { Button } from '@/components/ui'
import type { DocumentRow } from '../api/types'
import { checkTarget, customerCofa, explainCustomerCofa, type Tone } from '../utils/customer-cofa'
import { CofaIdentityChip } from './CofaIdentityChip'

const TONE_CLASS: Record<Tone, string> = {
  ok: 'border-success-border bg-success-bg',
  warn: 'border-warning-border bg-warning-bg',
  bad: 'border-danger-border bg-danger-bg',
  neutral: 'border-border-subtle bg-surface-sunk',
}

/** Top-of-drawer panel: the one CofA that matters for shipping, what the
 *  check found, and the actions that resolve it. */
export function CustomerCofaCheck({
  row,
  viewingCustomerCofa,
  canVerify,
  canAct,
  pending,
  onView,
  onVerify,
  onUnverify,
  onReplace,
}: {
  row: DocumentRow
  viewingCustomerCofa: boolean
  canVerify: boolean
  canAct: boolean
  pending: boolean
  onView: () => void
  onVerify: () => void
  onUnverify: () => void
  onReplace: () => void
}) {
  const { label: customerLabel, attachment: customerAttachment } = customerCofa(row)
  // Actions act on the certificate to fix: the customer CofA, or for an SPI
  // CofA built from the wrong vendor CofA, that vendor CofA.
  const { label, attachment } = checkTarget(row)
  const ex = explainCustomerCofa(row)
  const status = row.source_problem
    ? 'mismatch'
    : customerAttachment ? customerAttachment.identity?.status ?? 'pending' : 'missing'
  const blocking = status === 'mismatch' || status === 'unverifiable'
  return (
    <section
      data-testid="customer-cofa-check"
      className={`rounded-md border p-3 ${TONE_CLASS[ex.tone]}`}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="text-role-section-label">Customer CofA check · {customerLabel}</span>
        <CofaIdentityChip status={status} identity={customerAttachment?.identity} sourceProblem={row.source_problem} />
      </div>
      <div className="text-sm font-semibold text-primary">{ex.headline}</div>
      <p className="mt-1 text-sm text-secondary">{ex.detail}</p>
      {ex.todo ? (
        <p className="mt-1 text-sm text-primary">
          <span className="font-semibold">What to do: </span>
          {ex.todo}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {attachment && !viewingCustomerCofa ? (
          <Button variant="secondary" size="sm" onClick={onView}>
            <Eye size={12} /> Show the {label}
          </Button>
        ) : null}
        {canVerify && attachment && blocking ? (
          // On a wrong-product certificate the right move is usually to
          // replace it, so verifying is the secondary action there.
          <Button
            variant={status === 'mismatch' ? 'ghost' : 'primary'}
            size="sm"
            onClick={onVerify}
            disabled={pending}
          >
            <BadgeCheck size={12} /> {status === 'mismatch' ? 'Same material — mark verified' : 'Mark verified'}
          </Button>
        ) : null}
        {canVerify && status === 'verified_human' ? (
          <Button variant="ghost" size="sm" onClick={onUnverify} disabled={pending}>
            <Undo2 size={12} /> Revoke verification
          </Button>
        ) : null}
        {canAct && (blocking || status === 'missing') ? (
          <Button
            variant={status === 'mismatch' || status === 'missing' ? 'primary' : 'secondary'}
            size="sm"
            onClick={onReplace}
            disabled={pending}
          >
            <Upload size={12} /> {attachment ? `Replace ${label}` : `Upload ${label}`}
          </Button>
        ) : null}
      </div>
    </section>
  )
}
